import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabase-client';
import { LogSnagPost, slugifyString } from './helpers';
import { useRouter } from 'next/router';
import crypto from 'crypto';
import { PricingParams } from "@/components/PricingFeatures";

export const UserContext = createContext();

export const UserContextProvider = (props) => {
  const router = useRouter();
  const [userLoaded, setUserLoaded] = useState(false);
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [team, setTeam] = useState(null);
  const [userFinderLoaded, setUserFinderLoaded] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);

  useEffect(() => {
    const session = supabase.auth.session();
    setSession(session);
    setUser(session?.user ?? null);
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
      }
    );

    setUserFinderLoaded(true);

    return () => {
      authListener.unsubscribe();
    };
  }, []);

  // const getTeam = () => supabase.from('teams').select('*').single();

  const getTeam = async () => {
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('team_id')
      .eq('id', user?.id)
      .single();

    if (userError || !userData?.team_id) {
      return { data: null };
    }

    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('team_id', String(userData.team_id))
      .limit(1); // 👈 замість .single()

    if (error || !data || data.length === 0) {
      return { data: null };
    }

    return { data: data[0] }; // 👈 вручну витягуємо перший
  };


  const getUserDetails = () => supabase.from('users').select('*').eq('id', user?.id).single();
  const getSubscription = () =>
    supabase
      .from('subscriptions')
      .select('cancel_at, cancel_at_period_end, canceled_at, created, current_period_end, current_period_start, id, price_id, status, team_id, trial_end, trial_start, prices(*, products(*))')
      .in('status', ['trialing', 'active'])
      .order('created', { ascending: false })
      .limit(1)
      .single();

  useEffect(() => {
    if (user) {
      Promise.allSettled([getTeam(), getUserDetails(), getSubscription()]).then(
        async (results) => {
          const teamData = results[0].value?.data;
          const userDetailsTeamData = results[1].value.data?.team_id
          let actualTeam = 'none';

          if (teamData !== null) {
            actualTeam = {
              team_id: results[1].value.data?.team_id,
              team_name: results[1].value.data?.team_name
            };
            setTeam(actualTeam);
          } else if (userDetailsTeamData !== null) {
            actualTeam = userDetailsTeamData;
            setTeam(actualTeam);
          } else {
            setTeam('none');
          }

          setUserDetails(results[1].value.data);

          const teamIdForQuery =
            typeof actualTeam === 'string'
              ? actualTeam
              : (actualTeam && typeof actualTeam === 'object' ? actualTeam.team_id : null);


          if (results[2].value.data !== null) {
            setSubscription(results[2].value.data);
          } else {
            if (actualTeam !== 'none') {
              let { data, error } = await supabase
                .from('subscriptions')
                .select('cancel_at, cancel_at_period_end, canceled_at, created, current_period_end, current_period_start, id, price_id, status, team_id, trial_end, trial_start, prices(*, products(*))')
                .eq('team_id', teamIdForQuery)
                .in('status', ['trialing', 'active'])
                .order('created', { ascending: false })
                .maybeSingle();
              if (error) return error;
              setSubscription(data)
            }
          }
          if (results[2].value.data !== null) {
            setPlanDetails(results[2].value.data.prices.products.name.toLowerCase());
          } else {
            setPlanDetails('free');
          }

          setUserLoaded(true);
          setUserFinderLoaded(true);
        }
      );
    }
  }, [user]);

  // if(user && session && team === null && router?.pathname !== '/dashboard/create-team'){
  //   router.replace('/dashboard/create-team');
  // }

  const value = {
    session,
    user,
    team,
    userDetails,
    userLoaded,
    subscription,
    userFinderLoaded,
    planDetails,
    signIn: (options) => supabase.auth.signIn({ email: options.email }, { shouldCreateUser: options.shouldCreateUser, redirectTo: options.redirectTo }),
    signInWithPassword: (options) => supabase.auth.signIn({ email: options.email, password: options.password }, { shouldCreateUser: options.shouldCreateUser, redirectTo: options.redirectTo }),
    signUp: (options) => supabase.auth.signUp({ email: options.email, password: options.password }, { redirectTo: options.redirectTo, data: options.data }),
    forgotPassword: (email) => supabase.auth.api.resetPasswordForEmail(email),
    signOut: () => {
      setUserDetails(null);
      setSubscription(null);
      return supabase.auth.signOut();
    }
  };

  return <UserContext.Provider value={value} {...props} />;
};

export const useUser = () => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error(`useUser must be used within a UserContextProvider.`);
  }
  return context;
};

//Reset Password
export const resetPassword = async (token, password) => {
  const { error, data } = await supabase.auth.api
    .updateUser(token, { password: password })

  if (error) {
    return error;
  } else {
    return data
  }
};

//Get user account
export const getCompanies = async () => {
  const { data, error } = await supabase
    .from('companies')
    .select('active_company, company_affiliates, company_api_key, company_currency, company_handle, company_id, company_image, company_meta, company_name, company_url, created, crypto_wallet_address, description, domain_verified, id, payout_methods, skip_verified, stripe_id, team_id, company_country, integration_type, script_popup_config, script_popup_config_updated_at');

  if (error) {
    console.error('Error fetching companies:', error);
    return null;
  }
  return data;
};

//Get user campaigns
// export const getCampaigns = async (companyId) => {
//   const { data, error } = await supabase
//       .from('campaigns')
//       .select(`
//       *,
//       affiliates:affiliates!campaign_id (
//         count
//       ),
//       commissions:commissions!campaign_id (
//         commission_sale_value
//       )
//     `)
//       .eq('company_id', companyId)
//       .order('default_campaign', { ascending: false })
//       .order('created', { ascending: false });
//
//   if (error) return error;
//
//   // Обробка даних на клієнті
//   return data?.map(campaign => {
//     // Сума комісій
//     const commissionsSum = campaign.commissions
//         ?.reduce((acc, c) => acc + (c.commission_sale_value || 0), 0) || 0;
//
//     // Кількість афіліатів
//     const affiliatesCount = campaign.affiliates?.length || 0;
//
//     return {
//       ...campaign,
//       commissions_value: commissionsSum,
//       affiliate_count: affiliatesCount,
//     };
//   });
// };

export const getCampaigns = async (companyId) => {
  const { data, error } = await supabase
    .from('campaigns_with_stats') // Використовуємо View
    .select('*')
    .eq('company_id', companyId)
    .order('default_campaign', { ascending: false })
    .order('created', { ascending: false });

  if (error) return error;
  return data;
};

export const getCampaignById = async (campaignId) => {
  const { data, error } = await supabase
    .from('campaigns')
    .select('*')
    .eq('campaign_id', campaignId)
    .single();

  if (error) {
    console.error('Error fetching full campaign:', error);
    return null;
  }
  return data;
};

export const getAffiliates = async (companyId, page = 0, itemsPerPage = 20, filters = {}) => {
  const start = page * itemsPerPage;
  const end = start + itemsPerPage - 1;

  let query = supabase
    .from('affiliates_with_stats')
    .select('*', { count: 'exact' })
    .eq('company_id', companyId)
    .order('user_email', { ascending: true, nullsFirst: false })
    .range(start, end);

  // Додаємо фільтри до запиту
  if (filters.email) {
    query = query.or(`user_email.ilike.%${filters.email}%,invite_email.ilike.%${filters.email}%`);
  }

  if (filters.nickName) {
    query = query.ilike('nick_name', `%${filters.nickName}%`);
  }

  if (filters.status && filters.status !== 'all') {
    query = applyAffiliateStatusFilter(query, filters.status);
  }

  if (filters.campaign && filters.campaign !== 'all') {
    query = query.eq('campaign_id', filters.campaign);
  }

  if (filters.createdFrom) {
    query = query.gte('created', filters.createdFrom);
  }
  if (filters.createdTo) {
    query = query.lte('created', filters.createdTo);
  }

  const { data, error, count } = await query;

  if (error) return { data: null, error };
  return {
    data: {
      data: data || [],
      total: count || 0
    },
    error: null
  };
};

const PAGE = 1000;

const PENDING_AFFILIATE_OR_FILTER =
  'and(accepted.eq.false,manual_approved.eq.false),and(accepted.eq.false,manual_approved.eq.true,invited_user_id.is.null)';

function applyAffiliateStatusFilter(query, status) {
  if (status === 'active') {
    return query.eq('accepted', true);
  }
  if (status === 'pending') {
    return query.or(PENDING_AFFILIATE_OR_FILTER);
  }
  if (status === 'inactive') {
    return query
      .eq('accepted', false)
      .eq('manual_approved', true)
      .not('invited_user_id', 'is', null);
  }
  return query;
}

function applyCommonFilters(query, companyId, filters) {
  let q = query.eq('company_id', companyId);

  if (filters?.email) {
    q = q.or(`user_email.ilike.%${filters.email}%,invite_email.ilike.%${filters.email}%`);
  }
  if (filters?.nickName) {
    q = q.ilike('nick_name', `%${filters.nickName}%`);
  }
  if (filters?.status && filters.status !== 'all') {
    q = applyAffiliateStatusFilter(q, filters.status);
  }
  if (filters?.campaign && filters.campaign !== 'all') {
    const cid = Number(filters.campaign);
    if (!Number.isNaN(cid)) q = q.eq('campaign_id', cid);
  }

  if (filters?.createdFrom) {
    q = q.gte('created', filters.createdFrom);
  }

  if (filters?.createdTo) {
    q = q.lte('created', filters.createdTo);
  }
  return q;
}


export async function getAffiliateIdsByPreset(companyId, preset, filters = {}) {
  // базовий запит до view affiliates_with_stats
  let base = supabase
    .from('affiliates_with_stats')
    .select('affiliate_id', { count: 'exact' });

  base = applyCommonFilters(base, companyId, filters);

  // додаткові умови за пресетом
  switch (preset) {
    case 'active':
      base = applyAffiliateStatusFilter(base, 'active');
      break;
    case 'pending':
      base = applyAffiliateStatusFilter(base, 'pending');
      break;
    case 'inactive':
      base = applyAffiliateStatusFilter(base, 'inactive');
      break;
    case 'revenue_positive':
      base = base.gt('commissions_value', 0);
      break;
    case 'impressions_positive':
      base = base.gt('impressions', 0);
      break;
    case 'all':
    default:
      // без додаткових умов
      break;
  }

  // перший “сухий” виклик, щоб отримати count
  const { data: firstBatch, error: firstErr, count } = await base.range(0, 0);
  if (firstErr) return { ids: [], error: firstErr };

  const total = count || 0;
  if (total === 0) return { ids: [], error: null };

  // тепер збираємо всі айді батчами
  let ids = [];
  for (let from = 0; from < total; from += PAGE) {
    const to = Math.min(from + PAGE - 1, total - 1);
    const { data, error } = await base.range(from, to);
    if (error) return { ids: [], error };
    ids = ids.concat((data || []).map(r => r.affiliate_id));
  }
  // унікалізуємо на всяк випадок
  ids = Array.from(new Set(ids));
  return { ids, error: null };
}

/**
 * Підтягнути повні рядки по списку id для експорту (батчами).
 */
export async function getAffiliatesByIds(companyId, ids = []) {
  if (!ids.length) return { rows: [], error: null };
  const CHUNK = 1000;
  let rows = [];

  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    const { data, error } = await supabase
      .from('affiliates_with_stats')
      .select('*')
      .eq('company_id', companyId)
      .in('affiliate_id', chunk);

    if (error) return { rows: [], error };
    rows = rows.concat(data || []);
  }
  return { rows, error: null };
}

//Get user referrals
export const getReferrals = async (companyId, cursor, filters = {}) => {
  let query = supabase
    .from('referrals')
    .select(`
        *,
        campaign:campaign_id (campaign_name),
        affiliate:affiliate_id (affiliate_id,user_link,nick_name)
      `,
      { count: "exact" }
    )
    .eq('company_id', companyId)
    .order('created', { ascending: false })
    .limit(30);

  // Додаємо курсор для пагінації
  if (cursor) {
    query = query.lt('created', cursor);
  }

  // Фільтрація за статусом
  if (filters.status && filters.status !== 'all') {
    if (filters.status === 'visited-link') {
      query = query
        .is('referral_converted', false)
        .is('referral_reference_email', null)
        .gt('referral_expiry', new Date().toISOString());
    } else if (filters.status === 'expired') {
      query = query
        .lt('referral_expiry', new Date().toISOString())
        .is('referral_reference_email', null)
        .is('referral_converted', false);
    } else if (filters.status === 'signed-up') {
      query = query
        .not('referral_reference_email', 'is', null)
        .is('referral_converted', false);
    } else if (filters.status === 'converted') {
      query = query.is('referral_converted', true);
    }
  }

  if (filters.createdFrom) {
    query = query.gte('created', filters.createdFrom);
  }
  if (filters.createdTo) {
    query = query.lte('created', filters.createdTo);
  }

  // Фільтрація за кампанією
  if (filters.campaign && filters.campaign !== 'all') {
    query = query.eq('campaign_id', filters.campaign);
  }

  // Фільтрація за нікнеймом афіліата
  if (filters.nickName) {
    query = query.ilike('affiliate->>nick_name', `%${filters.nickName}%`);
  }

  // Фільтрація за ім'ям реферала
  if (filters.referralUsername) {
    query = query.ilike('referral_reference_email', `%${filters.referralUsername}%`);
  }

  const { data, count, error } = await query;

  if (error) {
    console.error('Error fetching referrals:', error);
    return "error";
  }

  return { data, count };
};

export const getSales = async (companyId, cursor, filters, campaignId = null) => {
  try {
    let query = supabase
      .from('commissions')
      .select(
        `
          *,
          campaign:campaign_id (campaign_name),
          affiliate:affiliate_id (details:invited_user_id(email)),
          affiliate_details:affiliate_id (
            affiliate_id,
            user_link,
            nick_name,
            user:invited_user_id (crypto_wallet_address, payout_crypto, paypal_email)
          ),
          referral_details:referral_id (referral_reference_email)
        `,
        { count: "exact" }
      )
      .eq('company_id', companyId)
      .order('created', { ascending: false })
      .limit(30);

    if (cursor !== null) {
      query = query.lt('created', cursor);
    }

    if (campaignId) {
      query = query.eq('campaign_id', campaignId);
    }

    // Додаємо фільтри до запиту
    const parseCurrency = (value) => {
      if (!value || value.trim() === '') return null;
      const cleanValue = value.replace(/,/g, '.');
      const hasDecimal = cleanValue.includes('.');

      if (hasDecimal) {
        const floatValue = parseFloat(cleanValue);
        return isNaN(floatValue) ? null : Math.round(floatValue * 100);
      }

      const intValue = parseInt(cleanValue, 10);
      return isNaN(intValue) ? null : intValue * 100;
    };

    const amountMinCents = parseCurrency(filters?.amountMin);
    const amountMaxCents = parseCurrency(filters?.amountMax);
    const commissionMinCents = parseCurrency(filters?.commissionDueMin);
    const commissionMaxCents = parseCurrency(filters?.commissionDueMax);

    if (amountMinCents !== null) {
      query = query.gte('commission_sale_value', amountMinCents);
    }
    if (amountMaxCents !== null) {
      query = query.lte('commission_sale_value', amountMaxCents);
    }
    if (commissionMinCents !== null) {
      query = query.gte('commission_total', commissionMinCents);
    }
    if (commissionMaxCents !== null) {
      query = query.lte('commission_total', commissionMaxCents);
    }
    if (filters?.products) {
      query = query.ilike('commission_description', `%${filters.products}%`);
    }
    if (filters?.referralIdentify) {
      query = query.ilike('referral_details.referral_reference_email', `%${filters.referralIdentify}%`);
    }

    if (filters?.createdFrom) {
      query = query.gte('created', filters.createdFrom);
    }
    if (filters?.createdTo) {
      const to = filters.createdTo.length === 10
        ? `${filters.createdTo}T23:59:59`
        : filters.createdTo;
      query = query.lte('created', to);
    }

    if (filters?.status && filters.status !== 'all') {
      if (filters.status === 'paid') {
        query = query.not('paid_at', 'is', null);
      } else if (filters.status === 'unpaid') {
        query = query.is('paid_at', null)
        // .lt('commission_due_date', new Date().toISOString());
      } else if (filters.status === 'not_due_yet') {
        query = query.is('paid_at', null)
          .gt('commission_due_date', new Date().toISOString());
      }
    }

    const { data, count, error } = await query;

    if (error) throw error;
    return { data, count };
  } catch (error) {
    console.error('Error fetching commissions:', error);
    return "error";
  }
};
export const getAfivioCommissionsDue = async (teamId) => {
  let query = supabase
    .from('commissions')
    .select(`
        *,
        campaign:campaign_id (campaign_name),
        affiliate:affiliate_id (details:invited_user_id(email,paypal_email))
      `,
      { count: "exact" }
    )
    .eq('team_id', teamId)
    .eq('afivio_commission_paid', false)
    .lt('commission_due_date', [((new Date()).toISOString())])
    .is('paid_at', null)
    .order('created', { ascending: false });

  const { data, error } = await query;

  if (error) return "error";
  return { data };
};

export const payCommissions = async (companyId, checkedCommissions, eligibleCommissions) => {
  if (!companyId || !eligibleCommissions) return "error";

  try {
    const selectedIds = checkedCommissions?.length ? new Set(checkedCommissions) : null;
    let candidateCommissions = eligibleCommissions;
    if (selectedIds) {
      const { data, error } = await supabase
        .from('commissions')
        .select('commission_id, company_id, paid_at, commission_due_date, external_provider, external_payload')
        .eq('company_id', companyId)
        .in('commission_id', [...selectedIds]);
      if (error) throw error;
      candidateCommissions = data || [];
    }

    const commissionsToPay = candidateCommissions.filter((commission) => {
      if (commission?.paid_at) return false;
      if (commission?.external_provider === 'awin' && !commission?.external_payload?.awin_paid_to_afivio_at) return false;
      return new Date(commission?.commission_due_date).getTime() <= Date.now();
    });
    if (!commissionsToPay.length) return "error";

    await Promise.all(commissionsToPay.map(async (item) => {
        const { error } = await supabase
          .from('commissions')
          .update({
            paid_at: ((new Date()).toISOString())
          })
          .eq('company_id', companyId)
          .eq('commission_id', item?.commission_id);
        if (error) throw error;
      }));

    return "success";

  } catch (error) {
    console.warn(error);
    return "error";

  }
};


export const payCommissionsByAffiliateId = async (companyId, userIds, date) => {
  if (!companyId || !userIds?.length || !date) return "error";

  try {
    // Визначення періоду
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
    const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);

    // Оновлення комісій для обраних афіліатів у вказаному місяці
    const { error } = await supabase
      .from('commissions')
      .update({ paid_at: new Date().toISOString() })
      .in('affiliate_id', userIds)
      .gte('created', monthStart.toISOString())
      .lte('created', monthEnd.toISOString())
      .is('paid_at', null); // Тільки неоплачені

    return error ? "error" : "success";
  } catch (error) {
    console.error("Commission payment error:", error);
    return "error";
  }
};


//Create company
export const newTeam = async (user, form) => {
  if (!form?.team_name) return "error";

  // const getTeam = await supabase.from('teams').select('*');
  //
  // if(getTeam?.data?.length > 0){
  //   console.log("Team already exists");
  //   return "error";
  // }

  // const { data } = await supabase.from('teams').insert({
  //   id: user?.id,
  //   team_name: form?.team_name
  // });

  const { data, error } = await supabase
    .from('teams')
    .insert(
      { id: user.id, team_name: form.team_name },
      { ignoreDuplicates: true }
    );

  if (error) {
    console.error("Błąd przy wstawianiu drużyny:", error);
    return "error";
  }

  // jeśli data jest puste, to znaczy, że drużyna już istniała
  if (!data?.length) {
    console.log("Team already exists");
    return "error";
  }

  if (data && data[0]?.team_id) {
    const userUpdate = await supabase
      .from('users')
      .update({
        team_id: data[0]?.team_id
      })
      .eq('id', user?.id);

    if (userUpdate?.data && userUpdate?.data !== null) {
      return "success";
    }
  }

  return "error";
};

export const addUserToTeam = async (userId, teamId, email) => {
  try {
    const { error } = await supabase
      .from('members')
      .update({
        member_user_id: userId
      })
      .eq('member_team_id', teamId)
      .eq('invite_email', email)
      .is('member_user_id', null);

    if (error) {
      console.error('Failed to add user to team:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Error adding user to team:', err);
    return false;
  }
};

export const generateApiKey = () => {
  return crypto.randomBytes(10).toString('hex');
};
export const newCompany = async (user, form) => {
  const checkLimit = await checkPlanLimits(user.team_id, 'companies');
  if (!checkLimit) {
    return { status: 409, message: 'Plan limit exceeded. Upgrade to increase limits.' };
  }

  if (!form?.company_handle || !form?.company_url || !form?.company_name) return "error";

  // Генеруємо API ключ
  const apiKey = generateApiKey();

  const { data, error } = await supabase.from('companies').insert({
    id: user?.id,
    team_id: user?.team_id,
    company_name: form?.company_name,
    company_url: form?.company_url,
    company_handle: form?.company_handle,
    domain_verified: false,
    company_api_key: apiKey,
    stripe_id: null,
    stripe_account_data: null,
    integration_type: form.integration_type || 'script_api',
    description: form?.description
  });

  if (error) {
    if (error?.code === "23505") {
      return "duplicate";
    }
    return "error";
  } else {
    return data;
  }
};

export const continueWithoutStripe = async (companyId) => {
  const { error } = await supabase
    .from('companies')
    .update({
      stripe_id: 'manual',
      stripe_account_data: 'manual'
    })
    .eq('company_id', companyId);

  return error ? "error" : "success";
}

export const handleActiveCompany = async (switchedCompany) => {
  let { data } = await supabase
    .from('companies')
    .select('active_company, company_affiliates, company_api_key, company_currency, company_handle, company_id, company_image, company_meta, company_name, company_url, created, crypto_wallet_address, description, domain_verified, id, payout_methods, stripe_id, team_id, script_popup_config, script_popup_config_updated_at')
    .eq('active_company', true);

  if (data) {
    data?.map(async company => {
      await supabase
        .from('companies')
        .update({
          active_company: false
        })
        .eq('company_id', company?.company_id);
    })
  }

  let { error } = await supabase
    .from('companies')
    .update({
      active_company: true
    })
    .eq('company_id', switchedCompany);
  if (error) return "error";

  return "success";
};

export const newCampaign = async (user, form, companyId) => {
  const checkLimit = await checkPlanLimits(user.team_id, 'campaigns');

  if (!checkLimit) {
    return { status: 409, message: 'Plan limit exceeded. Upgrade to increase limits.' };
  }
  try {
    let formFields = {
      ...form,
      id: user?.id,
      company_id: companyId,
      team_id: form?.team_id || user?.team_id
    };
    // Обробка типу винагороди
    if (formFields.commission_type === 'per_view') {
      // Валідація для Pay per View
      const min = Number(formFields.min_commission_value);
      const max = Number(formFields.max_commission_value);

      if (isNaN(min) || isNaN(max) || min <= 0 || max <= 0 || min > max) {
        throw new Error('Invalid commission range');
      }

      formFields.min_commission_value = min;
      formFields.max_commission_value = max;
      formFields.commission_value = null;

    } else if (formFields.commission_type === 'loyalty') {
      // Обробка Loyalty Program
      formFields.commission_value = null;
      formFields.min_commission_value = null;
      formFields.max_commission_value = null;

      // Перевірка обов'язкових полів
      if (!formFields.loyalty_condition) {
        throw new Error('Loyalty condition is required');
      }

      // Валідація дат
      const startDate = new Date(formFields.start_date);
      const endDate = new Date(formFields.end_date);
      if (startDate >= endDate) {
        throw new Error('End date must be after start date');
      }

      // Валідація умов лояльності
      if (formFields.loyalty_condition === 'discount') {
        if (
          !formFields.loyalty_min_purchase ||
          !formFields.loyalty_discount_percent ||
          formFields.loyalty_min_purchase <= 0 ||
          formFields.loyalty_discount_percent <= 0
        ) {
          throw new Error('Invalid discount loyalty parameters');
        }
      } else if (formFields.loyalty_condition === 'free_item') {
        if (
          !formFields.loyalty_required_purchases ||
          !formFields.loyalty_free_items ||
          formFields.loyalty_required_purchases <= 0 ||
          formFields.loyalty_free_items <= 0
        ) {
          throw new Error('Invalid free item loyalty parameters');
        }
      }

    } else {
      // Обробка для Percentage/Fixed
      formFields.min_commission_value = null;
      formFields.max_commission_value = null;

      if (!formFields.commission_value || formFields.commission_value <= 0) {
        formFields.commission_value = formFields.commission_type === 'fixed' ? 1 : 20;
      }
    }

    // Валідація додаткових полів
    formFields.cookie_window = formFields.cookie_window > 0 ? formFields.cookie_window : 60;
    formFields.commission_period = formFields.commission_period > 0 ? formFields.commission_period : 12;
    formFields.minimum_days_payout = formFields.minimum_days_payout >= 3 ? formFields.minimum_days_payout : 30;

    // Обробка знижок
    if (!formFields.discount_code?.trim()) {
      formFields.discount_code = null;
      formFields.discount_type = null;
      formFields.discount_value = null;
    }

    // Обробка дефолтної кампанії
    const { data: existingDefault } = await supabase
      .from('campaigns')
      .select('*')
      .eq('default_campaign', true)
      .eq('company_id', companyId);

    if (existingDefault?.length > 0 && formFields.default_campaign) {
      await supabase
        .from('campaigns')
        .update({ default_campaign: false })
        .eq('company_id', companyId);
    }

    // Конвертація дат для Supabase
    if (formFields.start_date) {
      formFields.start_date = new Date(formFields.start_date).toISOString();
    }
    if (formFields.end_date) {
      formFields.end_date = new Date(formFields.end_date).toISOString();
    }

    // Створення нової кампанії
    const { data: campaign, error } = await supabase
      .from('campaigns')
      .insert(formFields)
      .select('campaign_id')
      .single();

    if (error) throw error;

    await LogSnagPost('new-campaign', `New campaign ${formFields.campaign_name} created`);
    return { status: 'success', campaign_id: campaign?.campaign_id, legacy: 'success' };

  } catch (error) {
    console.error('Error creating campaign:', error);
    toast.error(error.message || 'Failed to add new campaign');
    return "error";
  }
};

export const editCampaign = async (user, campaignId, form) => {
  try {
    let formFields = { ...form };

    // Обробка типу винагороди
    if (formFields.commission_type === 'per_view') {
      const min = Number(formFields.min_commission_value);
      const max = Number(formFields.max_commission_value);

      if (isNaN(min) || isNaN(max) || min <= 0 || max <= 0 || min > max) {
        throw new Error('Invalid commission range');
      }

      formFields.min_commission_value = min;
      formFields.max_commission_value = max;
      formFields.commission_value = null;
    } else {
      formFields.min_commission_value = null;
      formFields.max_commission_value = null;

      if (!formFields.commission_value || formFields.commission_value <= 0) {
        formFields.commission_value = formFields.commission_type === 'fixed' ? 1 : 20;
      }
    }

    // Валідація додаткових полів
    formFields.cookie_window = formFields.cookie_window > 0 ? formFields.cookie_window : 60;
    formFields.commission_period = formFields.commission_period > 0 ? formFields.commission_period : 12;
    formFields.minimum_days_payout = formFields.minimum_days_payout >= 3 ? formFields.minimum_days_payout : 30;

    // Обробка знижок
    if (!formFields.discount_code?.trim()) {
      formFields.discount_code = null;
      formFields.discount_type = null;
      formFields.discount_value = null;
    }

    // Обробка дефолтної кампанії
    if (formFields.default_campaign) {
      await supabase
        .from('campaigns')
        .update({ default_campaign: false })
        .eq('company_id', formFields.company_id);
    }

    // Оновлення кампанії
    const { error } = await supabase
      .from('campaigns')
      .update(formFields)
      .eq('campaign_id', campaignId);

    if (error) throw error;

    return "success";

  } catch (error) {
    console.error('Error updating campaign:', error);
    return "error";
  }
};
export const editCampaignMeta = async (campaignId, metaData) => {
  if (!campaignId || !metaData) return "error";

  const { error } = await supabase
    .from('campaigns')
    .update({
      custom_campaign_data: metaData,
    }).
    eq('campaign_id', campaignId);

  if (error) return "error";

  return "success";
}

export const toggleCampaignStatus = async (campaignId, isActive) => {
  if (!campaignId) return "error";

  const updateData = isActive
    ? { end_date: new Date().toISOString() } // Деактивуємо
    : { end_date: null }; // Активуємо

  const { error } = await supabase
    .from('campaigns')
    .update(updateData)
    .eq('campaign_id', campaignId);

  if (error) return "error";
  return "success";
};


const TAG = '[saveCampaignTieredCommissionSettings]';

const normalizeTierKey = (name, fallback) => {
  const base = String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return base || fallback;
};

const asNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const saveCampaignTieredCommissionSettings = async ({
  teamId,
  companyId,
  campaignId,
  settings,
}) => {
  console.log(TAG, 'START', { teamId, companyId, campaignId, settings });

  try {
    if (!teamId) throw new Error('Missing teamId');
    if (!campaignId) throw new Error('Missing campaignId');

    // Optional feature: if disabled -> only turn off flag (no custom_campaign_data)
    if (!settings || settings?.enabled !== true) {
      console.log(TAG, 'DISABLED -> set campaign flag false');
      const { error: campErr } = await supabase
        .from('campaigns')
        .update({ tiered_commission_enabled: false })
        .eq('campaign_id', campaignId);

      if (campErr) {
        console.error(TAG, 'campaign flag update error', campErr);
        throw campErr;
      }

      console.log(TAG, 'DONE (disabled)');
      return { status: 'success', data: { disabled: true } };
    }

    // Map UI metric -> DB metric_type (matches SQL CHECK: sales_count/sales_amount)
    const uiMetric = settings.metricType;
    const metric_type = uiMetric === 'sale_amount' ? 'sales_amount' : 'sales_count';

    const effective_from = String(settings.effectiveFrom || '').slice(0, 10); // 'YYYY-MM-DD'
    console.log(TAG, 'NORMALIZED', { metric_type, effective_from });

    if (!/^\d{4}-\d{2}-\d{2}$/.test(effective_from)) {
      throw new Error('effectiveFrom must be a date string YYYY-MM-DD (use YYYY-MM-01 recommended)');
    }

    const inputTiers = Array.isArray(settings.tiers) ? settings.tiers : [];
    if (!inputTiers.length) throw new Error('No tiers provided');

    // Build rule set payload
    const ruleSetPayload = {
      team_id: teamId,
      company_id: companyId || null,
      campaign_id: campaignId,
      metric_type,
      effective_from,
      is_active: true,
    };

    console.log(TAG, 'UPSERT RULE SET payload', ruleSetPayload);

    const { data: ruleSet, error: rsErr } = await supabase
      .from('campaign_commission_rule_sets')
      .upsert(ruleSetPayload, { onConflict: 'team_id,campaign_id,effective_from' })
      .select('id, metric_type, effective_from')
      .single();

    if (rsErr) {
      console.error(TAG, 'UPSERT RULE SET error', rsErr);
      throw rsErr;
    }

    console.log(TAG, 'UPSERT RULE SET OK', ruleSet);

    // Replace tiers for this rule_set_id
    console.log(TAG, 'DELETE TIERS for rule_set_id', ruleSet.id);
    const { error: delErr } = await supabase
      .from('campaign_commission_tiers')
      .delete()
      .eq('rule_set_id', ruleSet.id);

    if (delErr) {
      console.error(TAG, 'DELETE TIERS error', delErr);
      throw delErr;
    }

    // Normalize tiers into DB schema
    // NOTE: UI tiers from your payload are: { name, threshold, commissionPercent, perks }
    const tierRows = inputTiers
      .map((t, idx) => {
        const name = String(t?.name || '').trim() || `Tier ${idx + 1}`;
        const tier_key = normalizeTierKey(name, `tier_${idx + 1}`);

        const threshold = asNumber(t?.threshold);
        if (threshold == null || threshold < 0) {
          throw new Error(`Invalid threshold for tier "${name}"`);
        }

        const commission_percent = asNumber(t?.commissionPercent);
        if (commission_percent == null || commission_percent <= 0 || commission_percent > 100) {
          throw new Error(`Invalid commissionPercent for tier "${name}"`);
        }

        const perks = String(t?.perks || '').trim();
        const benefits = perks ? { perks } : null;

        const row = {
          team_id: teamId,
          rule_set_id: ruleSet.id,
          tier_key,
          tier_name: name,
          threshold_count: null,
          threshold_amount_cents: null,
          commission_percent,
          benefits,
          sort_order: idx + 1,
        };

        if (metric_type === 'sales_count') {
          row.threshold_count = Math.floor(threshold);
        } else {
          // amount is entered in MAJOR units in UI -> store cents
          row.threshold_amount_cents = Math.round(threshold * 100);
        }

        return row;
      })
      // Optional: ensure sorted by threshold
      .sort((a, b) => {
        const av = metric_type === 'sales_count' ? a.threshold_count : a.threshold_amount_cents;
        const bv = metric_type === 'sales_count' ? b.threshold_count : b.threshold_amount_cents;
        return (av ?? 0) - (bv ?? 0);
      })
      // re-assign sort_order after sorting
      .map((r, i) => ({ ...r, sort_order: i + 1 }));

    console.log(TAG, 'INSERT TIERS rows', tierRows);

    const { data: inserted, error: insErr } = await supabase
      .from('campaign_commission_tiers')
      .insert(tierRows)
      .select('id');

    if (insErr) {
      console.error(TAG, 'INSERT TIERS error', insErr);
      throw insErr;
    }

    console.log(TAG, 'INSERT TIERS OK. inserted_count=', inserted?.length ?? null);

    // Enable campaign flag (NO custom_campaign_data)
    console.log(TAG, 'SET campaign.tiered_commission_enabled=true');
    const { error: campErr } = await supabase
      .from('campaigns')
      .update({ tiered_commission_enabled: true })
      .eq('campaign_id', campaignId);

    if (campErr) {
      console.error(TAG, 'campaign flag update error', campErr);
      throw campErr;
    }

    console.log(TAG, 'DONE SUCCESS', { rule_set_id: ruleSet.id });
    return { status: 'success', data: { rule_set_id: ruleSet.id } };
  } catch (e) {
    console.error(TAG, 'FAILED', e);
    return { status: 'error', error: e?.message || String(e) };
  }
};



export const fetchCampaignTieredCommissionSettings = async ({
  teamId,
  campaignId,
  currencyDecimals = 2
}) => {
  const TAG = '[fetchCampaignTieredCommissionSettings]';

  try {
    if (!teamId) throw new Error('Missing teamId');
    if (!campaignId) throw new Error('Missing campaignId');

    // беремо останній активний rule_set (найновіший effective_from)
    const { data: rsRows, error: rsErr } = await supabase
      .from('campaign_commission_rule_sets')
      .select('id, metric_type, effective_from, is_active')
      .eq('team_id', teamId)
      .eq('campaign_id', campaignId)
      .eq('is_active', true)
      .order('effective_from', { ascending: false })
      .limit(1);

    if (rsErr) throw rsErr;

    const ruleSet = rsRows?.[0];
    if (!ruleSet?.id) {
      return { status: 'success', settings: null }; // нічого не задано
    }

    const { data: tierRows, error: tErr } = await supabase
      .from('campaign_commission_tiers')
      .select('tier_name, threshold_count, threshold_amount_cents, commission_percent, benefits, sort_order')
      .eq('rule_set_id', ruleSet.id)
      .order('sort_order', { ascending: true });

    if (tErr) throw tErr;

    const metricType = ruleSet.metric_type === 'sales_amount' ? 'sale_amount' : 'sale_count';
    const div = Math.pow(10, currencyDecimals);

    const tiers = (tierRows || []).map((r) => {
      const threshold =
        metricType === 'sale_amount'
          ? Number(r.threshold_amount_cents ?? 0) / div
          : Number(r.threshold_count ?? 0);

      return {
        name: r.tier_name || 'Tier',
        threshold,
        commissionPercent: Number(r.commission_percent ?? 0),
        perks: r?.benefits?.perks ? String(r.benefits.perks) : ''
      };
    });

    return {
      status: 'success',
      settings: {
        enabled: true,
        metricType,
        effectiveFrom: String(ruleSet.effective_from || '').slice(0, 10),
        tiers
      }
    };
  } catch (e) {
    console.error(TAG, 'FAILED', e);
    return { status: 'error', error: e?.message || String(e) };
  }
};

const currentMonthKeyUTC = (now = new Date()) => {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  return d.toISOString().slice(0, 10);
};


export const getAffiliatesCurrentCommissionPercentBatch = async ({
  items, // [{ affiliate_id, campaign_id }, ...]
  now = new Date(),
}) => {
  const TAG = '[getAffiliatesCurrentCommissionPercentBatch]';

  try {
    const rows = Array.isArray(items) ? items : [];
    const affiliateIds = [...new Set(rows.map(r => r?.affiliate_id).filter(Boolean))];
    const campaignIds = [...new Set(rows.map(r => r?.campaign_id).filter(Boolean))];

    if (!affiliateIds.length || !campaignIds.length) {
      return { status: 'success', byAffiliateId: {}, benefitsByAffiliateId: {}, month: currentMonthKeyUTC(now) };
    }

    // 1) campaigns: tiered flag + base commission_value
    const { data: campaigns, error: campErr } = await supabase
      .from('campaigns')
      .select('campaign_id, tiered_commission_enabled, commission_value')
      .in('campaign_id', campaignIds);

    if (campErr) throw campErr;

    const campaignById = new Map((campaigns || []).map(c => [c.campaign_id, c]));
    const month = currentMonthKeyUTC(now);

    // 2) affiliate_commission_monthly: беремо commission_percent + tier_id для поточного місяця
    // ВАЖЛИВО: commission_percent беремо саме звідси (як ти просив)
    const { data: acm, error: acmErr } = await supabase
      .from('affiliate_commission_monthly')
      .select('affiliate_id, tier_id, commission_percent')
      .in('affiliate_id', affiliateIds)
      .eq('month', month);

    if (acmErr) throw acmErr;

    const monthlyPctByAffiliateId = new Map();
    const tierIdByAffiliateId = new Map();

    for (const r of (acm || [])) {
      if (!r?.affiliate_id) continue;

      const pct = Number(r.commission_percent);
      if (Number.isFinite(pct)) {
        monthlyPctByAffiliateId.set(r.affiliate_id, pct);
      }

      if (r.tier_id) {
        tierIdByAffiliateId.set(r.affiliate_id, r.tier_id);
      }
    }

    // 3) campaign_commission_tiers: тягнемо benefits по tier_id (для тих, у кого є tier_id)
    const tierIds = [...new Set(Array.from(tierIdByAffiliateId.values()).filter(Boolean))];

    let benefitsByTierId = new Map();
    if (tierIds.length) {
      const { data: tiers, error: tierErr } = await supabase
        .from('campaign_commission_tiers')
        .select('id, benefits') // ✅ тільки benefits (можна додати commission_percent як запасний fallback)
        .in('id', tierIds);

      if (tierErr) throw tierErr;

      benefitsByTierId = new Map((tiers || []).map(t => [t.id, t?.benefits ?? null]));
    }

    // 4) build result per affiliate_id (з урахуванням tiered flag)
    const byAffiliateId = {};
    const benefitsByAffiliateId = {};

    for (const it of rows) {
      const aid = it?.affiliate_id;
      const cid = it?.campaign_id;
      if (!aid || !cid) continue;

      const camp = campaignById.get(cid);
      const tiered = camp?.tiered_commission_enabled === true;
      const base = Number(camp?.commission_value);
      const basePct = Number.isFinite(base) ? base : null;

      if (!tiered) {
        byAffiliateId[aid] = basePct;
        benefitsByAffiliateId[aid] = null;
        continue;
      }

      // tiered=true: percent з monthly, якщо немає — fallback на base
      const pct = monthlyPctByAffiliateId.has(aid) ? monthlyPctByAffiliateId.get(aid) : null;
      byAffiliateId[aid] = Number.isFinite(pct) ? pct : basePct;

      const tierId = tierIdByAffiliateId.get(aid);
      benefitsByAffiliateId[aid] = tierId ? (benefitsByTierId.get(tierId) ?? null) : null;
    }

    return { status: 'success', byAffiliateId, benefitsByAffiliateId, month };
  } catch (e) {
    console.error(TAG, e);
    return { status: 'error', error: e?.message || String(e) };
  }
};



//New Stripe Account
export const newStripeAccount = async (userId, stripeId, companyId, accessToken) => {
  try {
    const token = accessToken || supabase.auth.session()?.access_token || null;

    const response = await fetch('/api/get-account-details', {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ accountId: stripeId })
    });

    const result = await response.json();

    // Перевіряємо успішність запиту
    if (!result.success || !result.data) {
      console.error('Failed to retrieve Stripe account:', result.error);
      return "error";
    }

    const { error } = await supabase
      .from('companies')
      .update({
        stripe_account_data: result.data, // Використовуємо отримані дані
        stripe_id: stripeId
      })
      .eq('company_id', companyId);

    if (error) {
      console.error('Supabase update error:', error);
      return "error";
    }

    await LogSnagPost('stripe-connected', `New Stripe account connected for company ${companyId}`);
    return "success";
  } catch (error) {
    console.error('Unexpected error in newStripeAccount:', error);
    return "error";
  }
};

export const manuallyVerifyDomain = async (companyId) => {
  let { error } = await supabase
    .from('companies')
    .update({
      domain_verified: true
    })
    .eq('company_id', companyId);

  if (error) return "error";

  return "success";
}

export const manuallySkipVerifyDomain = async (companyId) => {
  let { error } = await supabase
    .from('companies')
    .update({
      skip_verified: true
    })
    .eq('company_id', companyId);

  if (error) return "error";
  await manuallyVerifyDomain(companyId);
  await continueWithoutStripe(companyId);

  return "success";
};


export const deleteAffiliate = async (
  affiliateId,
  {
    companyId = null,
    reasonDeleted = null,
    deletedBy = null,
  } = {}
) => {
  try {
    // 1. Забираємо affiliate
    const { data: affiliateRows, error: affiliateError } = await supabase
      .from('affiliates')
      .select('id, affiliate_id, company_id, campaign_id, team_id, accepted, created, invited_user_id')
      .eq('affiliate_id', affiliateId)
      .limit(1);

    const affiliate = affiliateRows?.[0] || null;

    if (affiliateError || !affiliate) {
      console.error('Affiliate not found or error:', affiliateError);
      return 'error';
    }

    // companyId опційний, але якщо ти захочеш звіряти:
    if (companyId && affiliate.company_id && affiliate.company_id !== companyId) {
      console.error('Company mismatch while deleting affiliate');
      return 'error';
    }

    // 2. Забираємо referrals цього affiliate (може бути порожньо)
    const { data: referrals, error: referralsError } = await supabase
      .from('referrals')
      .select('referral_id, campaign_id, referral_converted, created, subscription_id')
      .eq('affiliate_id', affiliateId);

    if (referralsError) {
      console.error('Error fetching referrals:', referralsError);
      return 'error';
    }

    // 3. Забираємо commissions цього affiliate (теж може бути порожньо)
    const { data: commissions, error: commissionsError } = await supabase
      .from('commissions')
      .select('commission_id, commission_total, commission_sale_value, commission_refund_value, paid_at, created')
      .eq('affiliate_id', affiliateId);

    if (commissionsError) {
      console.error('Error fetching commissions:', commissionsError);
      return 'error';
    }

    const deletedAt = new Date().toISOString();
    const logs = [];

    logs.push({
      company_id: affiliate.company_id,
      campaign_id: affiliate.campaign_id,
      team_id: affiliate.team_id,
      affiliate_id: affiliate.affiliate_id,
      affiliate_created: affiliate.created,
      accepted: affiliate.accepted,

      referral_id: null,
      referral_converted: null,
      referral_created: null,
      subscription_id: null,

      commission_id: null,
      commission_total: null,
      commission_sale_value: null,
      commission_refund_value: null,
      paid_at: null,
      commission_created: null,

      reason_deleted: reasonDeleted,
      deleted_at: deletedAt,
    });

    // 4.2. Один рядок на кожен referral
    (referrals || []).forEach((r) => {
      logs.push({
        affiliate_id: affiliate.affiliate_id,
        company_id: affiliate.company_id,
        accepted: affiliate.accepted,
        team_id: affiliate.team_id,
        affiliate_created: affiliate.created,

        referral_id: r.referral_id,
        campaign_id: r.campaign_id,
        referral_converted: r.referral_converted,
        referral_created: r.created,
        subscription_id: r.subscription_id,

        commission_id: null,
        commission_total: null,
        commission_sale_value: null,
        commission_refund_value: null,
        paid_at: null,
        commission_created: null,

        deleted_by: deletedBy,
        reason_deleted: reasonDeleted,
        deleted_at: deletedAt,
      });
    });

    // 4.3. Один рядок на кожну commission
    (commissions || []).forEach((c) => {
      logs.push({
        affiliate_id: affiliate.affiliate_id,
        company_id: affiliate.company_id,
        accepted: affiliate.accepted,
        team_id: affiliate.team_id,
        affiliate_created: affiliate.created,

        referral_id: null,
        campaign_id: null,
        referral_converted: null,
        referral_created: null,
        subscription_id: null,

        commission_id: c.commission_id,
        commission_total: c.commission_total,
        commission_sale_value: c.commission_sale_value,
        commission_refund_value: c.commission_refund_value,
        paid_at: c.paid_at,
        commission_created: c.created,

        deleted_by: deletedBy,
        reason_deleted: reasonDeleted,
        deleted_at: deletedAt,
      });
    });

    // 5. Записуємо все в entity_deletions
    const { error: logError } = await supabase
      .from('entity_deletions')
      .insert(logs);

    if (logError) {
      console.error('Error inserting delete logs:', logError);
      return 'error'; // не видаляємо, якщо не злогувалось
    }

    // 6. Видаляємо в правильному порядку:
    //    commissions -> referrals -> affiliates

    // 6.1 commissions
    const { error: deleteCommissionsError } = await supabase
      .from('commissions')
      .delete()
      .eq('affiliate_id', affiliateId);

    if (deleteCommissionsError) {
      console.error('Error deleting commissions:', deleteCommissionsError);
      return 'error';
    }

    // 6.2 referrals
    const { error: deleteReferralsError } = await supabase
      .from('referrals')
      .delete()
      .eq('affiliate_id', affiliateId);

    if (deleteReferralsError) {
      console.error('Error deleting referrals:', deleteReferralsError);
      return 'error';
    }

    // 6.3 loyalty progress (FK loyalty_progress_affiliate_id_fkey -> affiliates.affiliate_id)
    const { error: deleteLoyaltyProgressError } = await supabase
      .from('loyalty_progress')
      .delete()
      .eq('affiliate_id', affiliateId);

    if (deleteLoyaltyProgressError) {
      console.error('Error deleting loyalty progress:', deleteLoyaltyProgressError);
      return 'error';
    }

    // 6.4 Якщо є інший affiliate цього ж користувача в компанії, переносимо conversation на нього
    let replacementAffiliate = null;
    let replacementAffiliateError = null;
    let conversationOwnerUserId = affiliate.id || null;
    let useInvitedUserId = false;

    if (affiliate.invited_user_id) {
      const { data: invitedUserRows, error: invitedUserError } = await supabase
        .from('users')
        .select('id, user_type')
        .eq('id', affiliate.invited_user_id)
        .limit(1);

      const invitedUserRow = invitedUserRows?.[0] || null;

      if (!invitedUserError && invitedUserRow?.user_type === 'affiliate') {
        conversationOwnerUserId = affiliate.invited_user_id;
        useInvitedUserId = true;
      }
    }

    if (conversationOwnerUserId) {
      let replacementQuery = supabase
        .from('affiliates')
        .select('affiliate_id, created')
        .eq('company_id', affiliate.company_id)
        .eq('accepted', true)
        .neq('affiliate_id', affiliateId)
        .order('created', { ascending: true })
        .limit(1);

      if (useInvitedUserId) {
        replacementQuery = replacementQuery.eq('invited_user_id', conversationOwnerUserId);
      } else {
        replacementQuery = replacementQuery.eq('id', conversationOwnerUserId);
      }

      const response = await replacementQuery;

      replacementAffiliate = response.data?.[0] || null;
      replacementAffiliateError = response.error;
    }

    if (replacementAffiliateError) {
      console.error('Error loading replacement affiliate:', replacementAffiliateError);
      return 'error';
    }

    if (replacementAffiliate?.affiliate_id) {
      const { error: reassignConversationError } = await supabase
        .from('conversations')
        .update({ affiliate_user_id: conversationOwnerUserId })
        .eq('company_id', affiliate.company_id)
        .eq('affiliate_user_id', conversationOwnerUserId);

      if (reassignConversationError) {
        console.error('Error reassigning conversation affiliate:', reassignConversationError);
        return 'error';
      }
    }

    // 6.5 affiliate
    const { error: deleteAffiliateError } = await supabase
      .from('affiliates')
      .delete()
      .eq('affiliate_id', affiliateId);

    if (deleteAffiliateError) {
      console.error('Error deleting affiliate:', deleteAffiliateError);
      return 'error';
    }

    return 'success';
  } catch (e) {
    console.error('Unexpected deleteAffiliate error:', e);
    return 'error';
  }
};

export const deleteCommission = async (id) => {
  const { error } = await supabase
    .from('commissions')
    .delete()
    .match({ commission_id: id })

  if (error) {
    return "error";
  } else {
    return "success";
  }
};

export const deleteCompany = async (companyId, { reasonDeleted = null, deletedBy = null } = {}) => {
  const { error } = await supabase.rpc('delete_company_full', {
    p_company_id: companyId,
    p_reason_deleted: reasonDeleted,
    p_deleted_by: deletedBy,
  });

  if (error) {
    console.error('delete_company_full error:', error);
    return 'error';
  }
  return 'success';
};

export const editCurrency = async (companyId, data) => {
  if (!data?.company_currency) {
    return "error";
  }

  const { error } = await supabase
    .from('companies')
    .update({
      company_currency: data?.company_currency
    })
    .eq('company_id', companyId);

  if (error) return "error";

  return "success";
};

export async function editCompanyCountry(companyId, countryCode) {
  if (!companyId) return 'error';
  const code = String(countryCode || '').toUpperCase();
  const isValid = code === 'WORLD' || /^[A-Z]{2}$/.test(code);
  if (!isValid) return 'error';

  const { error } = await supabase
    .from('companies')
    .update({ company_country: code === 'WORLD' ? 'World' : code })
    .eq('company_id', companyId);

  if (error) return 'error';
  return 'success';
}

export const updateIntegrationType = async (companyId, integrationType) => {
  const { data, error } = await supabase
    .from('companies')
    .update({
      integration_type: integrationType,
      updated: new Date().toISOString()
    })
    .eq('company_id', companyId);

  if (error) {
    console.error('Error updating integration type:', error);
    return error.message;
  }

  return "success";
};

const COOKIE_POPUP_DEFAULTS = Object.freeze({
  showpopupagreement: false,
  showpopupreward: false,
  showpopuprewardaffiliatecode: false,
  popup_primary_color: '#5a8bff',
  popup_accent_color: '#5de4ff',
  popup_surface_color: '#121b35',
  popup_agreement_title: 'Cookies for better functionality',
  popup_agreement_subtitle: 'We use cookie to improve your experience.',
  popup_reward_title: 'Your discount code',
  popup_reward_subtitle: 'Copy this code and use it at checkout.',
  popup_affiliate_title: 'Your checkout code',
  popup_affiliate_subtitle: 'Copy and paste this code when placing your order.',
  popup_agreement_primary_button_text: 'Accept cookies',
  popup_agreement_secondary_button_text: 'No thanks',
  popup_reward_primary_button_text: 'Accept cookies and show code',
  popup_reward_secondary_button_text: 'No thanks',
  popup_reward_copy_button_text: 'Copy code and close',
  popup_reward_close_button_text: 'Close',
  popup_affiliate_primary_button_text: 'Accept cookies and show code',
  popup_affiliate_secondary_button_text: 'No thanks',
  popup_affiliate_copy_button_text: 'Copy code and close',
  popup_affiliate_close_button_text: 'Close'
});

const toBooleanFlag = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  }
  return fallback;
};

const sanitizePopupColor = (value, fallback) => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return /^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed : fallback;
};

const sanitizePopupText = (value, fallback, maxLength = 140, allowEmpty = false) => {
  if (typeof value !== 'string') return fallback;
  const sanitized = value.replace(/[\u0000-\u001F\u007F]/g, '').trim();
  if (!sanitized) return allowEmpty ? '' : fallback;
  return sanitized.slice(0, maxLength);
};

const normalizePopupSettings = (input = {}) => {
  const merged = {
    ...COOKIE_POPUP_DEFAULTS,
    ...(input && typeof input === 'object' ? input : {})
  };
  const agreementSecondaryText =
    typeof merged.popup_agreement_secondary_button_text === 'string'
      ? merged.popup_agreement_secondary_button_text
      : merged.popup_reward_secondary_button_text;

  const normalized = {
    showpopupagreement: toBooleanFlag(merged.showpopupagreement, COOKIE_POPUP_DEFAULTS.showpopupagreement),
    showpopupreward: toBooleanFlag(merged.showpopupreward, COOKIE_POPUP_DEFAULTS.showpopupreward),
    showpopuprewardaffiliatecode: toBooleanFlag(
      merged.showpopuprewardaffiliatecode,
      COOKIE_POPUP_DEFAULTS.showpopuprewardaffiliatecode
    ),
    popup_primary_color: sanitizePopupColor(merged.popup_primary_color, COOKIE_POPUP_DEFAULTS.popup_primary_color),
    popup_accent_color: sanitizePopupColor(merged.popup_accent_color, COOKIE_POPUP_DEFAULTS.popup_accent_color),
    popup_surface_color: sanitizePopupColor(merged.popup_surface_color, COOKIE_POPUP_DEFAULTS.popup_surface_color),
    popup_agreement_title: sanitizePopupText(merged.popup_agreement_title, COOKIE_POPUP_DEFAULTS.popup_agreement_title),
    popup_agreement_subtitle: sanitizePopupText(
      merged.popup_agreement_subtitle,
      COOKIE_POPUP_DEFAULTS.popup_agreement_subtitle,
      220
    ),
    popup_reward_title: sanitizePopupText(merged.popup_reward_title, COOKIE_POPUP_DEFAULTS.popup_reward_title),
    popup_reward_subtitle: sanitizePopupText(
      merged.popup_reward_subtitle,
      COOKIE_POPUP_DEFAULTS.popup_reward_subtitle,
      220
    ),
    popup_affiliate_title: sanitizePopupText(merged.popup_affiliate_title, COOKIE_POPUP_DEFAULTS.popup_affiliate_title),
    popup_affiliate_subtitle: sanitizePopupText(
      merged.popup_affiliate_subtitle,
      COOKIE_POPUP_DEFAULTS.popup_affiliate_subtitle,
      220
    ),
    popup_agreement_primary_button_text: sanitizePopupText(
      merged.popup_agreement_primary_button_text,
      COOKIE_POPUP_DEFAULTS.popup_agreement_primary_button_text,
      60,
      true
    ),
    popup_agreement_secondary_button_text: sanitizePopupText(
      agreementSecondaryText,
      COOKIE_POPUP_DEFAULTS.popup_agreement_secondary_button_text,
      60,
      true
    ),
    popup_reward_primary_button_text: sanitizePopupText(
      merged.popup_reward_primary_button_text,
      COOKIE_POPUP_DEFAULTS.popup_reward_primary_button_text,
      60,
      true
    ),
    popup_reward_secondary_button_text: sanitizePopupText(
      merged.popup_reward_secondary_button_text,
      COOKIE_POPUP_DEFAULTS.popup_reward_secondary_button_text,
      60,
      true
    ),
    popup_reward_copy_button_text: sanitizePopupText(
      merged.popup_reward_copy_button_text,
      COOKIE_POPUP_DEFAULTS.popup_reward_copy_button_text,
      60,
      true
    ),
    popup_reward_close_button_text: sanitizePopupText(
      merged.popup_reward_close_button_text,
      COOKIE_POPUP_DEFAULTS.popup_reward_close_button_text,
      60,
      true
    ),
    popup_affiliate_primary_button_text: sanitizePopupText(
      merged.popup_affiliate_primary_button_text,
      COOKIE_POPUP_DEFAULTS.popup_affiliate_primary_button_text,
      60,
      true
    ),
    popup_affiliate_secondary_button_text: sanitizePopupText(
      merged.popup_affiliate_secondary_button_text,
      COOKIE_POPUP_DEFAULTS.popup_affiliate_secondary_button_text,
      60,
      true
    ),
    popup_affiliate_copy_button_text: sanitizePopupText(
      merged.popup_affiliate_copy_button_text,
      COOKIE_POPUP_DEFAULTS.popup_affiliate_copy_button_text,
      60,
      true
    ),
    popup_affiliate_close_button_text: sanitizePopupText(
      merged.popup_affiliate_close_button_text,
      COOKIE_POPUP_DEFAULTS.popup_affiliate_close_button_text,
      60,
      true
    )
  };

  if (normalized.showpopupreward && normalized.showpopuprewardaffiliatecode) {
    normalized.showpopuprewardaffiliatecode = false;
  }

  return normalized;
};

export const editCompanyCookiePopupSettings = async (id, popupSettings, currentCompanyMeta = {}) => {
  if (!id) return 'error';

  const normalizedSettings = normalizePopupSettings(popupSettings);
  const safeMeta =
    currentCompanyMeta && typeof currentCompanyMeta === 'object' && !Array.isArray(currentCompanyMeta)
      ? currentCompanyMeta
      : {};

  const { error } = await supabase
    .from('companies')
    .update({
      script_popup_config: normalizedSettings,
      script_popup_config_updated_at: new Date().toISOString(),
      company_meta: {
        ...safeMeta,
        cookie_popup_settings: normalizedSettings
      }
    })
    .match({ company_id: id });

  if (error) {
    console.error('Error updating cookie popup settings:', error);
    return 'error';
  }

  return 'success';
};

export const editCompanyWebsite = async (id, form) => {
  const { error } = await supabase
    .from('companies')
    .update({
      company_url: form?.company_url,
      domain_verified: false
    })
    .match({ company_id: id })

  if (error) {
    return "error";
  } else {
    return "success";
  }
};

export const editCompanyHandle = async (id, form) => {
  if (!form?.company_handle) return "error";

  const { error } = await supabase
    .from('companies')
    .update({
      company_handle: slugifyString(form?.company_handle)
    })
    .match({ company_id: id })

  if (error) {

    if (error?.code === "23505") {
      return "duplicate"
    }

    return "error";
  } else {
    return "success";
  }
};


export const editCompanyDescription = async (id, form) => {
  // Перевірка, чи передано поле company_description і чи не перевищує його довжина 2000 символів
  if (!form?.company_description || form.company_description.length > 2000) return "error";

  const { error } = await supabase
    .from('companies')
    .update({
      description: form.company_description
    })
    .match({ company_id: id });

  if (error) {
    return "error";
  } else {
    return "success";
  }
};


export const disableEmails = async (id, type) => {
  const { error } = await supabase
    .from('companies')
    .update({ disable_emails: type })
    .match({ company_id: id })

  if (error) {
    return "error";
  } else {
    return "success";
  }
};

export const archiveSubmission = async (id, type) => {
  const { error } = await supabase
    .from('submissions')
    .update({ archived: type })
    .match({ submission_id: id })

  if (error) {
    return "error";
  } else {
    return "success";
  }
};

//Upload logo to log
export const uploadLogoImage = async (companyId, file) => {
  const modifiedId = companyId?.replace('_', '-');
  const { data, error } = await supabase.storage
    .from('company-assets')
    .upload(`${modifiedId}/${file.name}`, file, {
      cacheControl: '0',
      upsert: true
    })

  console.log("error:")
  console.log(error)

  if (error) return error;

  if (data?.Key) {
    const { error } = await supabase
      .from('companies')
      .update({
        company_image: data?.Key,
      }).eq('company_id', companyId);

    if (error) return error;
  }

  return data;
};

export const manualAffiliateCommissionCreate = async (
  affiliate_reference,
  campaignId,
  commission_value,
  description
) => {
  try {
    // 1. Отримання афілієта з таблиці affiliates
    const { data: affiliates, error: affiliateError } = await supabase
      .from('affiliates')
      .select('*')
      .eq('campaign_id', campaignId)
      .or(`referral_code.eq.${affiliate_reference},affiliate_id.eq.${affiliate_reference}, invite_email.eq.${affiliate_reference}`);

    // Обробка помилок запиту
    if (affiliateError) {
      console.error('Affiliate query error:', affiliateError);
      return { error: { type: 'database_error', details: affiliateError.message } };
    }

    // Перевірка кількості знайдених афілієтів
    if (!affiliates || affiliates.length === 0) {
      return { error: { type: 'affiliate_not_found' } };
    }

    if (affiliates.length > 1) {
      return { error: { type: 'multiple_affiliates_found' } };
    }

    const affiliate = affiliates[0];

    // 2. Перевірка активності партнера
    if (affiliate.accepted !== true) {
      return {
        error: {
          type: 'affiliate_inactive',
          affiliate_id: affiliate.affiliate_id
        }
      };
    }
    const commission_value_major = commission_value * 100

    // 3. Створення комісії
    const { data: commission, error: commissionError } = await supabase
      .from('commissions')
      .insert({
        id: affiliate.invited_user_id,
        team_id: affiliate.team_id,
        campaign_id: affiliate.campaign_id,
        company_id: affiliate.company_id,
        affiliate_id: affiliate.affiliate_id,
        commission_sale_value: null,
        commission_total: commission_value_major,
        commission_due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        commission_description: description
      })
      .select('commission_id, commission_total, commission_due_date, commission_description')
      .single();

    if (commissionError) throw commissionError;

    return { data: commission };

  } catch (error) {
    console.error('Affiliate Commission Creation Error:', error);
    return { error: { type: 'database_error', details: error.message } };
  }
};

export const getLoyaltyParticipants = async (
  campaignId,
  cursor = null,
  limit = 30
) => {
  try {
    let progressQuery = supabase
      .from('loyalty_progress')
      .select(
        `*,
         affiliate_details:affiliate_id (
           affiliate_id,
           referral_code,
           user_link,
           nick_name
         )`,
        { count: 'exact' }
      )
      .eq('campaign_id', campaignId)
      .order('created', { ascending: false })
      .limit(limit);

    // cursor = значення колонки created останнього запису з попередньої сторінки
    if (cursor) {
      progressQuery = progressQuery.lt('created', cursor);
    }

    const {
      data: progressData,
      error: progressError,
      count,
    } = await progressQuery;

    if (progressError) throw progressError;

    const { data: campaignData, error: campaignError } = await supabase
      .from('campaigns')
      .select(`
        campaign_name,
        commission_type,
        loyalty_condition,
        loyalty_min_purchase,
        loyalty_discount_percent,
        loyalty_required_purchases,
        loyalty_free_items,
        start_date,
        end_date
      `)
      .eq('campaign_id', campaignId)
      .single();

    if (campaignError) throw campaignError;

    let nextCursor = null;
    if (progressData && progressData.length) {
      const last = progressData[progressData.length - 1];
      nextCursor = last.created || null;
      // last.loyalty_id також буде, але курсор нам зручніше по created
    }

    return {
      data: {
        loyaltyProgress: progressData || [],
        campaignInfo: campaignData || {},
        count: typeof count === 'number' ? count : null,
        nextCursor,
      },
      error: null,
    };
  } catch (error) {
    console.error('Error fetching loyalty data:', error);
    return { data: null, error };
  }
};

export const updateLoyaltyProgress = async (affiliate_reference, campaignId, activityData) => {
  try {
    // 1. Знаходимо афіліата за переданим ключем
    const { data: affiliates, error: affiliateError } = await supabase
      .from('affiliates')
      .select('id, affiliate_id, invited_user_id')
      .eq('campaign_id', campaignId)
      .or(`referral_code.eq.${affiliate_reference},affiliate_id.eq.${affiliate_reference},invite_email.eq.${affiliate_reference}`);

    if (affiliateError) throw new Error('Database error when try to find affiliate');
    if (!affiliates?.length) throw new Error('Affiliate not found');
    if (affiliates.length > 1) throw new Error('Multiple affiliates found');

    const affiliate = affiliates[0];

    // 2. Забираємо налаштування кампанії
    const { data: campaign, error: campaignError } = await supabase
      .from('campaigns')
      .select('loyalty_condition, loyalty_required_purchases, loyalty_discount_percent, team_id')
      .eq('campaign_id', campaignId)
      .single();

    if (campaignError) throw new Error('Campaign not found');

    // 3. Отримуємо останній запис прогресу для цього афіліата в цій кампанії
    const { data: lastRecords, error: lastRecordError } = await supabase
      .from('loyalty_progress')
      .select('total_loyalty_sale_value, total_loyalty_save_value, total_progress, total_claims_count, created')
      .eq('campaign_id', campaignId)
      .eq('affiliate_id', affiliate.affiliate_id)
      .order('created', { ascending: false })
      .limit(1);

    // Ініціалізуємо змінні (0, якщо записів ще не було)
    let lastTotalSale = 0;
    let lastTotalSave = 0;
    let previousCycleProgress = 0;
    let previousClaims = 0;

    if (!lastRecordError && lastRecords?.length) {
      const last = lastRecords[0];
      lastTotalSale = Number(last.total_loyalty_sale_value) || 0;
      lastTotalSave = Number(last.total_loyalty_save_value) || 0;
      previousCycleProgress = Number(last.total_progress) || 0;
      previousClaims = Number(last.total_claims_count) || 0;
    }

    // 4. Умова free_item
    if (campaign.loyalty_condition === 'free_item') {
      const currentSaleValue = activityData.saleValue ? Number(activityData.saleValue) : 1;
      const cumulativeProgress = previousCycleProgress + currentSaleValue;
      const additionalClaims = Math.floor(cumulativeProgress / campaign.loyalty_required_purchases);
      const newCycleProgress = cumulativeProgress % campaign.loyalty_required_purchases;
      const newTotalClaims = previousClaims + additionalClaims;

      const { error: insertError } = await supabase
        .from('loyalty_progress')
        .insert({
          id: affiliate.invited_user_id ?? affiliate.id,
          campaign_id: campaignId,
          affiliate_id: affiliate.affiliate_id,
          progress: currentSaleValue,
          total_progress: newCycleProgress,
          total_claims_count: newTotalClaims,
          team_id: campaign.team_id,
          created: new Date().toISOString(),
          loyalty_sale_value: currentSaleValue,
          loyalty_save_value: 0,
          total_loyalty_sale_value: lastTotalSale + currentSaleValue,
          total_loyalty_save_value: lastTotalSave,
        });

      if (insertError) throw insertError;
    }

    // 5. Умова discount (знижка відсотками)
    if (campaign.loyalty_condition === 'discount' && activityData?.saleValue) {
      const currentSaleForDiscount = Number(activityData.saleValue);
      const currentSaveValue = currentSaleForDiscount * (campaign.loyalty_discount_percent / 100);

      const { error: insertError } = await supabase
        .from('loyalty_progress')
        .insert({
          id: affiliate.invited_user_id ?? affiliate.id,
          campaign_id: campaignId,
          affiliate_id: affiliate.affiliate_id,
          progress: currentSaleForDiscount,
          total_progress: currentSaleForDiscount,
          total_claims_count: 0,
          team_id: campaign.team_id,
          created: new Date().toISOString(),
          loyalty_sale_value: currentSaleForDiscount,
          loyalty_save_value: currentSaveValue,
          total_loyalty_sale_value: lastTotalSale + currentSaleForDiscount,
          total_loyalty_save_value: lastTotalSave + currentSaveValue,
        });

      if (insertError) throw insertError;
    }

    return { success: true };
  } catch (error) {
    console.error('Loyalty Error:', error.message);
    return { error: error.message };
  }
};



export const billingUserUsageDetails = async (teamId) => {
  try {
    let companyData = await supabase
      .from('companies')
      .select('active_company, company_affiliates, company_api_key, company_currency, company_handle, company_id, company_image, company_meta, company_name, company_url, created, crypto_wallet_address, description, domain_verified, id, payout_methods, stripe_id, team_id, script_popup_config, script_popup_config_updated_at', { count: 'exact' })
      .eq('team_id', teamId);

    let campaignData = await supabase
      .from('campaigns')
      .select('*', { count: 'exact' })
      .eq('team_id', teamId);

    let affiliateData = await supabase
      .from('affiliates')
      .select('*', { count: 'exact' })
      .eq('team_id', teamId);

    let referralData = await supabase
      .from('referrals')
      .select('*', { count: 'exact' })
      .eq('team_id', teamId);

    let commissionData = await supabase
      .from('commissions')
      .select('*', { count: 'exact' })
      .eq('team_id', teamId);

    let teamMembersData = await supabase
      .from('members')
      .select('*', { count: 'exact' })
      .eq('member_team_id', teamId);

    return {
      companies: companyData?.count,
      campaigns: campaignData?.count,
      affiliates: affiliateData?.count,
      referrals: referralData?.count,
      commissions: commissionData?.count,
      members: teamMembersData?.count
    };
  } catch (error) {
    console.warn(error);
    return 'error';
  }
};

export const checkPlanLimits = async (teamId, featureType) => {
  const { data: subscription, error } = await supabase
    .from('subscriptions')
    .select('price_id')
    .eq('team_id', teamId)
    .order('created', { ascending: false })
    .limit(1)
    .single();

  if (!subscription?.price_id) {
    if (featureType === 'companies') {
      const { count } = await supabase
        .from('companies')
        .select('*', { count: 'exact' })
        .eq('team_id', teamId);
      return count === 0;
    }
    if (featureType === 'campaigns') {
      const { count } = await supabase
        .from('campaigns')
        .select('*', { count: 'exact' })
        .eq('team_id', teamId);
      return count === 0;
    }
    return false;
  }
  const { data: prices } = await supabase
    .from('prices')
    .select('product_id')
    .eq('id', subscription.price_id)
    .single();
  const { data: products } = await supabase
    .from('products')
    .select('name')
    .eq('product_id', prices.product_id)
    .single();
  const planName = products?.name?.toLowerCase() || 'free';
  const limits = PricingParams()[planName];
  const usage = await billingUserUsageDetails(teamId);
  if (featureType === 'crypto') {
    if (limits[featureType] === 0) return false;

    if (limits[featureType] === 1) {
      return usage[featureType] < 1;
    }
  }
  if (limits[featureType] === 'unlimited') return true;

  if (usage[featureType] >= limits[featureType]) {
    return false;
  }
  return true;
};

export const getPendingAffiliates = async (campaignId) => {
  try {
    const { data, error } = await supabase
      .from('affiliates')
      .select(`
        id,
        affiliate_id,
        created,
        manual_approved,
        nick_name,
        referral_code,
        user_id:users (
          id,
          email
        )
      `)
      .eq('campaign_id', campaignId)
      .eq('manual_approved', false)
      .order('created', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching pending affiliates:', error);
    return [];
  }
};


export const changeReferralCode = async (affiliateId, newCode) => {
  try {
    const { error } = await supabase
      .from('affiliates')
      .update({ referral_code: newCode })
      .eq('affiliate_id', affiliateId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating referral code:', error);
    throw error;
  }
};


export const approveAffiliate = async (affiliateId) => {
  try {
    if (!affiliateId) {
      throw new Error('Missing affiliate id');
    }

    const token = supabase.auth.session()?.access_token || null;
    const response = await fetch(`/api/affiliates/${encodeURIComponent(affiliateId)}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Error approving affiliate');
    }

    return true;
  } catch (error) {
    console.error('Error approving affiliate:', error);
    throw error;
  }
};

export const getQueueErrors = async (companyId) => {
  const { data, error } = await supabase
    .from('invite_queue')
    .select('*')
    .eq('company_id', companyId)
    .eq('status', 'error');

  if (error) {
    console.error('Error fetching queue errors:', error);
    return [];
  }
  return data || [];
};

export const deleteQueueItem = async (id) => {
  const { error } = await supabase
    .from('invite_queue')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting queue item:', error);
    return false;
  }
  return true;
};

export const checkActiveSubscription = async (userId) => {
  try {
    const { data: userTeam, errorUser } = await supabase
      .from('users')
      .select('team_id')
      .eq('id', userId)
      .maybeSingle();

    if (!userTeam) {
      console.log('No user found for team:', userTeam);
      return false;
    }

    const { data: subscription, error } = await supabase
      .from('subscriptions')
      .select('status, current_period_end, trial_end')
      .eq('team_id', userTeam.team_id)
      .order('created', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!subscription) {
      console.log('No subscription found for team:', userTeam);
      return false;
    }

    // Перевіряємо статус підписки
    const currentDate = new Date();
    const periodEnd = new Date(subscription.current_period_end);
    const trialEnd = subscription.trial_end ? new Date(subscription.trial_end) : null;

    const isActiveStatus = ['active', 'trialing'].includes(subscription.status);
    const isNotExpired = periodEnd > currentDate;
    const isTrialActive = trialEnd ? trialEnd > currentDate : false;

    return isActiveStatus && (isNotExpired || isTrialActive);
  } catch (error) {
    console.error('Subscription check error:', error);
    return false;
  }
};

export const useAdmin = () => {
  const { user } = useUser();
  const adminEmails = process.env.NEXT_PUBLIC_ADMIN_EMAILS?.split(',') || [];

  return {
    isAdmin: user && adminEmails.includes(user.email)
  };
};
